/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {finalize} from 'rxjs';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import * as _ from 'lodash';

import template from './email-translation-edit-page.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {LOCALES} from '../../common/constants/locales';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {
  applyDefaultEmailTranslationContent,
  getEmailTranslationTeamQueryParams,
  getEmailTranslationTypes,
  indexDefaultEmailTranslations,
  initEmailTranslationModel,
  normalizeEmailTemplateSelection,
  prepareEmailTranslationForSubmit,
  updateEmailTranslationInfo
} from './email-translation-commons';
import {
  EmailTranslationApiService,
  EmailTranslationModel,
  EmailUserInterface
} from './email-translation-api.service';
import {EmailTemplateModel, EmailTemplateTeam} from '../email-template/email-template-api.service';
import {AppSharedModule} from '../../common/app-shared.module';
import {CodemirrorFieldComponent} from '../../common/directives/codemirror-field.component';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule, ButtonsModule, CodemirrorFieldComponent],
  selector: 'adn-email-translation-edit-page',
  template
})
export class EmailTranslationEditPageComponent extends SignalAdminResourceEditPageBase<EmailTranslationModel> {
  readonly locales = LOCALES;
  readonly isMarketplace: boolean;
  readonly isAdmin: boolean;
  readonly emailTypes: string[];
  readonly applications: string[];
  readonly allTeams = signal<EmailTemplateTeam[]>([]);
  readonly teamsLoaded = signal(false);
  readonly userInterfaces = signal<EmailUserInterface[]>([]);
  readonly userInterfacesLoaded = signal(false);
  readonly availableTemplates = signal<EmailTemplateModel[]>([]);
  readonly templatesLoaded = signal(false);
  readonly defaultTranslationsByType = signal<Record<string, any>>({});
  readonly demoSuccess = signal(false);
  readonly demoError = signal(false);
  readonly demoPending = signal(false);
  demo = '';

  private readonly emailApi: EmailTranslationApiService;
  private readonly localUserProfile: LocalUserProfile;
  private readonly emailDestroyRef: DestroyRef;

  constructor() {
    const api = inject(EmailTranslationApiService);
    super(api, 'emailTranslation');
    this.emailApi = api;
    this.emailDestroyRef = inject(DestroyRef);
    const networkProfile = inject(LocalNetworkProfile);
    const permissions = inject(LocalUserPermissions);
    this.localUserProfile = inject(LocalUserProfile);
    this.isMarketplace = networkProfile.isPureMarketplacePlatform();
    this.isAdmin = !!this.localUserProfile.get().supportUserId;
    this.demo = this.localUserProfile.get('username');
    this.emailTypes = getEmailTranslationTypes(networkProfile);
    this.applications = permissions.getApps();
    this.loadTeams(getEmailTranslationTeamQueryParams(permissions));
    this.loadUserInterfaces(networkProfile.getNetworkId());
    this.loadDefaultTranslations();
    this.loadEmailTemplates(true);
  }

  get showTeamField(): boolean {
    return this.isMarketplace || !!this.editingModel?.team?.id;
  }

  compareTeams(team1: EmailTemplateTeam, team2: EmailTemplateTeam): boolean {
    return team1 && team2 ? team1.id === team2.id : team1 === team2;
  }

  hasApplication(app: string): boolean {
    return _.includes(this.editingModel?.applications, app);
  }

  toggleApplication(app: string, event: Event): void {
    if (!_.isArray(this.editingModel.applications)) {
      this.editingModel.applications = [];
    }
    const checked = (event.target as HTMLInputElement).checked;
    if (checked && !this.hasApplication(app)) {
      this.editingModel.applications.push(app);
    } else if (!checked) {
      _.pull(this.editingModel.applications, app);
    }
    this.markExternalFormDirty();
  }

  onMessageDefinitionChange(): void {
    applyDefaultEmailTranslationContent(this.editingModel, this.defaultTranslationsByType());
    this.markExternalFormDirty();
  }

  onSpecifyFieldChange(): void {
    applyDefaultEmailTranslationContent(this.editingModel, this.defaultTranslationsByType());
    this.markExternalFormDirty();
  }

  sendDemo(): void {
    if (this.demoPending()) {
      return;
    }
    this.demoSuccess.set(false);
    this.demoError.set(false);
    this.demoPending.set(true);
    const destination = this.localUserProfile.get().supportUserId ? this.demo : '';
    this.emailApi.sendTest(this.editingModel.id, destination).pipe(
      finalize(() => this.demoPending.set(false)),
      takeUntilDestroyed(this.emailDestroyRef)
    ).subscribe({
      next: () => this.demoSuccess.set(true),
      error: () => this.demoError.set(true)
    });
  }

  protected override initialiseModel(model: EmailTranslationModel): EmailTranslationModel {
    const initialised = super.initialiseModel(model);
    initEmailTranslationModel(initialised, this.defaultTranslationsByType());
    this.refreshAvailableTemplates();
    return initialised;
  }

  protected override afterSave(model: EmailTranslationModel): void {
    updateEmailTranslationInfo(model, this.defaultTranslationsByType());
    applyDefaultEmailTranslationContent(model, this.defaultTranslationsByType());
    normalizeEmailTemplateSelection(model, model);
    this.refreshAvailableTemplates();
  }

  protected override prepareForSubmit(model: EmailTranslationModel): void {
    prepareEmailTranslationForSubmit(model, this.defaultTranslationsByType());
  }

  private loadTeams(params: Record<string, any>): void {
    this.emailApi.listTeams(params).pipe(
      finalize(() => this.teamsLoaded.set(true)),
      takeUntilDestroyed(this.emailDestroyRef)
    ).subscribe({
      next: (page) => this.allTeams.set(page.results || []),
      error: () => undefined
    });
  }

  private loadUserInterfaces(networkId: string): void {
    this.emailApi.getUserInterfaces(networkId).pipe(
      finalize(() => this.userInterfacesLoaded.set(true)),
      takeUntilDestroyed(this.emailDestroyRef)
    ).subscribe({
      next: (page) => this.userInterfaces.set(page.results || []),
      error: () => undefined
    });
  }

  private loadDefaultTranslations(): void {
    this.emailApi.getDefaultTranslations().pipe(
      takeUntilDestroyed(this.emailDestroyRef)
    ).subscribe({
      next: (defaults) => {
        this.defaultTranslationsByType.set(indexDefaultEmailTranslations(defaults));
        initEmailTranslationModel(this.editingModel, this.defaultTranslationsByType());
      },
      error: () => undefined
    });
  }

  private loadEmailTemplates(initial: boolean): void {
    this.emailApi.listEmailTemplates().pipe(
      finalize(() => initial && this.templatesLoaded.set(true)),
      takeUntilDestroyed(this.emailDestroyRef)
    ).subscribe({
      next: (page) => this.availableTemplates.set(this.filterAvailableTemplates(page.results || [])),
      error: () => undefined
    });
  }

  private refreshAvailableTemplates(): void {
    if (this.templatesLoaded()) {
      this.loadEmailTemplates(false);
    }
  }

  private filterAvailableTemplates(templates: EmailTemplateModel[]): EmailTemplateModel[] {
    const selectedTemplateId = _.get(this.editingModel, ['emailTemplate', 'id']) || this.editingModel?.emailTemplate;
    return templates.filter((template) => {
      return !(template.validationWarnings || []).length || selectedTemplateId === template.id;
    });
  }
}
